import { useEverflowSdk } from "src/hooks/useEverflowSdk";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SingleSelect from "src/components/SingleSelect";
import { CreateLinkSourceId } from "src/helpers/Enums";

/** listecle product.redirectUrl — Schedsmart offer.destination_url */
const getLandingPageBaseUrl = (offer) => {
  const raw = offer?.destination_url?.trim?.() ?? "www.google.com";
  if (!raw) return "";
  try {
    const url = new URL(raw.includes("://") ? raw : `https://${raw}`);
    return `${url.origin}${url.pathname}`.replace(/\/$/, "") || url.origin;
  } catch {
    return raw.split("?")[0];
  }
};

const buildTrackingUrlParams = ({
  offerId,
  affiliateId,
  sourceId,
  sub1,
  sub2,
  sub3,
  sub4,
}) => ({
  oid: offerId,
  affid: affiliateId,
  source_id: sourceId,
  sub1: sub1 || "",
  sub2: sub2 || "",
  sub3: sub3 || "",
  sub4: sub4 || "",
});

const appendQueryParams = (baseUrl, queryParams) => {
  if (!baseUrl) return "";
  const [path, existingQs] = baseUrl.split("?");
  const params = new URLSearchParams(existingQs || "");
  queryParams.forEach((value, key) => {
    if (value != null && value !== "") params.set(key, value);
  });
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
};

const buildDirectUrl = (baseUrl, transactionId) => {
  if (!baseUrl || !transactionId) return "";
  const params = new URLSearchParams();
  params.set("_ef_transaction_id", String(transactionId));
  return appendQueryParams(baseUrl, params);
};

/** EF.click() resolves to transaction id string; API JSON may use { transaction_id }. */
const getTransactionIdFromClickResult = (result) => {
  if (!result) return "";
  if (typeof result === "string") return result;
  return result.transaction_id ?? "";
};

const CreateLink = ({ item, close, onCopyAndUse }) => {
  const { loaded } = useEverflowSdk();
  const [isSaving, setIsSaving] = useState(false);
  const calendarId = item?._id ?? item?.id ?? "";
  const offerId = item?.offer_id?.link_id ?? "";
  const [affiliateId, setAffiliateId] = useState("");
  const [useSub5, setUseSub5] = useState(false);
  const [activePreviewButton, setActivePreviewButton] = useState("first");
  const [openSourceSelect, setOpenSourceSelect] = useState(false);
  const [selectedSource, setSelectedSource] = useState(
    Object.values(CreateLinkSourceId)[0] || ""
  );
  const [sub1, setSub1] = useState("");
  const [sub2, setSub2] = useState("");
  const [sub3, setSub3] = useState("");
  const [sub4, setSub4] = useState("");
  const [efDirectUrl, setEfDirectUrl] = useState("");
  const activePreviewButtonRef = useRef(activePreviewButton);
  activePreviewButtonRef.current = activePreviewButton;
  const sourceList = Object.values(CreateLinkSourceId).map((sourceId) => ({
    name: sourceId,
  }));

  const selectSource = (source) => {
    setSelectedSource(source?.name || "");
  };

  const landingBaseUrl = getLandingPageBaseUrl(item?.offer_id);
  const redirectBaseUrl =
    item?.offer_id?.destination_url?.trim?.() || "wwww.google.com";

  const urlParams = useMemo(
    () =>
      buildTrackingUrlParams({
        offerId,
        affiliateId,
        sourceId: selectedSource,
        sub1,
        sub2,
        sub3,
        sub4,
      }),
    [offerId, affiliateId, selectedSource, sub1, sub2, sub3, sub4]
  );

  const handleClick = useCallback(
    async (trackingUrlParams) => {
      const {
        oid,
        affid,
        source_id,
        sub1: s1,
        sub2: s2,
        sub3: s3,
        sub4: s4,
      } = trackingUrlParams;

      const tid = await window.EF.click({
        offer_id: oid,
        affiliate_id: affid,
        sub1: s1,
        sub2: s2,
        sub3: s3,
        sub4: s4 || "",
        sub5: useSub5 ? calendarId : null,
        source_id,
      });

      return tid;
    },
    [loaded, calendarId, item, useSub5]
  );

  useEffect(() => {
    if (activePreviewButton !== "second") return;
    if (!loaded || !landingBaseUrl) return;

    let cancelled = false;

    handleClick(urlParams).then((tid) => {
      const transactionId = getTransactionIdFromClickResult(tid);
      if (cancelled || !transactionId) return;
      setEfDirectUrl(buildDirectUrl(landingBaseUrl, transactionId));
    });

    return () => {
      cancelled = true;
    };
  }, [activePreviewButton, loaded, landingBaseUrl, urlParams, handleClick]);

  useEffect(() => {
    if (activePreviewButtonRef.current !== "second") return;
    setActivePreviewButton("first");
    setEfDirectUrl("");
  }, [selectedSource, affiliateId, sub1, sub2, sub3, sub4, useSub5]);

  const redirectQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    if (selectedSource) params.set("source_id", selectedSource);
    if (sub1) params.set("sub1", sub1);
    if (sub2) params.set("sub2", sub2);
    if (sub3) params.set("sub3", sub3);
    if (sub4) params.set("sub4", sub4);
    if (useSub5 && calendarId) params.set("sub5", calendarId);
    if (offerId) params.set("oid", String(offerId));
    if (affiliateId) params.set("affid", affiliateId);
    return params;
  }, [
    selectedSource,
    sub1,
    sub2,
    sub3,
    sub4,
    useSub5,
    calendarId,
    offerId,
    affiliateId,
  ]);

  const redirectUrl = appendQueryParams(redirectBaseUrl, redirectQueryParams);

  const directUrl = activePreviewButton === "second" ? efDirectUrl : "";

  const previewUrl = activePreviewButton === "first" ? redirectUrl : directUrl;

  const handleCopyAndUse = () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      onCopyAndUse?.({
        directUrl,
        redirectUrl,
        previewUrl,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="NewStrategyModalOverlay bg-transparent">
      <div className="DeleteCalendarModal">
        <div className="w-full flex flex-col items-start gap-3 self-stretch">
          <div className="flex justify-between self-stretch">
            <div
              className={
                "flex h-8 px-3 items-center gap-[6px] rounded-lg bg-[#F6F6F9] text-[#15151F] font-['Inter'] text-xs font-semibold leading-normal"
              }
            >
              <img
                alt={"Logo"}
                src={"/media/icons/clockGray.svg"}
                className={"w-[18px] h-[18px]"}
              />
              Create Link
            </div>
            <button className={"CloseIconButton"} onClick={close}>
              <img
                alt={"Logo"}
                src={"/media/icons/closeIcon.svg"}
                className={"w-5 h-5"}
              />
            </button>
          </div>
          <div
            className={
              "flex p-[6px] justify-center items-start gap-2 rounded-lg bg-[#F6F6F9]"
            }
          >
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1">
                <div>
                  <img
                    alt={"Logo"}
                    src={"/media/icons/offer.svg"}
                    className={"w-[14px] h-[14px]"}
                  />
                </div>
                <div className="text-[#8185A8] font-['Inter'] text-xs font-medium leading-[18px]">
                  Offer Name:
                </div>
              </div>
              <div className="text-[#15151F] font-['Inter'] text-xs font-semibold leading-[18px]">
                {item?.offer_id?.name ?? ""}
              </div>
            </div>
          </div>
        </div>
        <div className={"HorizontalDivider"}></div>
        <div className={"NewStrategyInputSection"}>
          <div className={"NewStrategyInputTitle"}>Source ID</div>
          {openSourceSelect ? (
            <SingleSelect
              setOpenSelect={setOpenSourceSelect}
              selectedItem={selectedSource}
              selectItem={(source) => {
                selectSource(source);
                setOpenSourceSelect(false);
              }}
              list={sourceList}
              noIcon
            />
          ) : (
            <button
              className={"TagSelect"}
              onClick={() => setOpenSourceSelect(true)}
            >
              {selectedSource || "Select"}
              <img
                alt={"Logo"}
                src={"/media/icons/chevronDown.svg"}
                className={"w-[18px] h-[18px]"}
              />
            </button>
          )}
        </div>
        <div className="flex items-start gap-2 self-stretch">
          <div className={"NewStrategyInputSection"}>
            <div className={"NewStrategyInputTitle"}>Affiliate ID</div>
            <input
              className={"NewStrategyInput"}
              value={affiliateId}
              placeholder="Enter"
              onChange={(e) => setAffiliateId(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-start gap-2 self-stretch">
          <div className={"NewStrategyInputSection"}>
            <div className={"NewStrategyInputTitle"}>Sub1</div>
            <input
              className={"NewStrategyInput"}
              value={sub1}
              placeholder="Enter"
              onChange={(e) => setSub1(e.target.value)}
            />
          </div>
          <div className={"NewStrategyInputSection"}>
            <div className={"NewStrategyInputTitle"}>Sub2</div>
            <input
              className={"NewStrategyInput"}
              value={sub2}
              placeholder="Enter"
              onChange={(e) => setSub2(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-start gap-2 self-stretch">
          <div className={"NewStrategyInputSection"}>
            <div className={"NewStrategyInputTitle"}>Sub3</div>
            <input
              className={"NewStrategyInput"}
              value={sub3}
              placeholder="Enter"
              onChange={(e) => setSub3(e.target.value)}
            />
          </div>
          <div className={"NewStrategyInputSection"}>
            <div className={"NewStrategyInputTitle"}>Sub4</div>
            <input
              className={"NewStrategyInput"}
              value={sub4}
              placeholder="Enter"
              onChange={(e) => setSub4(e.target.value)}
            />
          </div>
        </div>
        <div className="flex p-3 flex-col items-start gap-2 self-stretch rounded-lg bg-[#F6F6F9]">
          <div className="text-[#171717] font-['Inter'] text-xs font-semibold leading-normal">
            Sub5
          </div>
          <div className="flex items-start gap-2 self-stretch">
            <div className="flex h-9 px-2 justify-between items-center flex-1 rounded-lg border border-[#E5E7F0] bg-[#F6F6F9]">
              <div className="text-[#8185A8] font-['Inter'] text-xs font-medium leading-normal">
                {calendarId || "—"}
              </div>
              <img
                alt={"id"}
                src={"/media/icons/copy-03.svg"}
                className={"w-4 h-4"}
              />
            </div>
            <button
              className="flex h-9 px-6 py-[7px] justify-center items-center gap-[10px] rounded-lg border border-[#D9D9D9] bg-white"
              onClick={() => setUseSub5((prev) => !prev)}
              aria-pressed={useSub5}
              type="button"
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border ${
                  useSub5
                    ? "border-[#7F56D9] bg-[#F9F5FF]"
                    : "border-[#D0D5DD] bg-white"
                }`}
              >
                {useSub5 && (
                  <img
                    alt="Logo"
                    src="/media/icons/check.svg"
                    className="w-3 h-3"
                  />
                )}
              </span>
              <span className="NewStrategyInputTitle">Use Sub5</span>
            </button>
          </div>
        </div>
        <div className="flex p-3 flex-col items-start gap-2 self-stretch rounded-lg bg-[#F6F6F9]">
          <div className="flex justify-between items-center self-stretch">
            <div className="text-[#171717] font-['Inter'] text-[11px] font-medium leading-normal tracking-[0.88px] uppercase">
              Generated Link Preview
            </div>
            <div className="flex p-[2px] items-center gap-[2px] rounded-md bg-white">
              <button
                className={`flex py-1 justify-center items-center gap-[10px] rounded text-center font-['Inter'] text-[11px] font-semibold leading-normal ${
                  activePreviewButton === "first"
                    ? "px-3 bg-[#F6F6F9] text-[#15151F]"
                    : "px-2 bg-white text-[#8185A8]"
                }`}
                onClick={() => setActivePreviewButton("first")}
              >
                Redirect Link
              </button>
              <button
                className={`flex py-1 justify-center items-center gap-[10px] rounded text-center font-['Inter'] text-[11px] font-semibold leading-normal ${
                  activePreviewButton === "second"
                    ? "px-3 bg-[#F6F6F9] text-[#15151F]"
                    : "px-2 bg-white text-[#8185A8]"
                }`}
                onClick={() => setActivePreviewButton("second")}
              >
                Direct Link
              </button>
            </div>
          </div>
          <div className="text-[#15151F] font-['JetBrains_Mono'] text-xs font-medium leading-[18px] break-all">
            {previewUrl}
          </div>
        </div>
        <div className={"HorizontalDivider"}></div>
        <div className={"flex justify-between items-center self-stretch"}>
          <button className={"SecondaryButton"} onClick={close}>
            <img
              alt={"Logo"}
              src={"/media/icons/arrowLeft.svg"}
              className={"w-4 h-4"}
            />
            Back
          </button>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={"SecondaryButton"}
              onClick={() => {
                if (previewUrl)
                  window.open(previewUrl, "_blank", "noopener,noreferrer");
              }}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/link-external-01.svg"}
                className={"w-4 h-4"}
              />
              Test Link
            </button>
            <button
              type="button"
              className={"PrimaryButton"}
              disabled={isSaving}
              onClick={handleCopyAndUse}
            >
              <img
                alt={"Logo"}
                src={"/media/icons/copy-02.svg"}
                className={"w-4 h-4"}
              />
              {isSaving ? "Saving..." : "Copy & Use"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateLink;
